import { useState, useEffect, useCallback } from "react";

import { useAuth } from "../contexts/AuthContext";
import { supabase } from "../services/supabase";
import { Station } from "../types";

export function useFavorites() {
  const { user } = useAuth();
  const [favorites, setFavorites] = useState<Station[]>(() => {
    // Initial load from localStorage for immediate display
    try {
      const saved = localStorage.getItem("radio_favorites");
      return saved ? JSON.parse(saved) : [];
    } catch (e) {
      console.error("Failed to parse favorites from localStorage", e);
      return [];
    }
  });
  const [loading, setLoading] = useState(false);

  // Sync favorites when user logs in
  useEffect(() => {
    if (!user) return;

    const syncFavorites = async () => {
      if (!supabase) {
        console.warn("[useFavorites] Supabase client not available");
        return;
      }
      setLoading(true);
      try {
        // 1. Fetch remote favorites
        const { data: remoteFavorites, error } = await supabase
          .from("user_favorites")
          .select("*")
          .order("created_at", { ascending: false });

        if (error) throw error;

        // 2. Check for local favorites to merge
        const localFavorites = JSON.parse(
          localStorage.getItem("radio_favorites") || "[]",
        );

        if (localFavorites.length > 0) {
          // Identify favorites that are local but not remote
          const remoteIds = new Set(remoteFavorites?.map((f) => f.item_id));
          const newLocalFavorites = localFavorites.filter(
            (f: Station) => !remoteIds.has(f.id),
          );

          if (newLocalFavorites.length > 0) {
            // Upload new local favorites
            const { error: uploadError } = await supabase
              .from("user_favorites")
              .insert(
                newLocalFavorites.map((station: Station) => ({
                  user_id: user.id,
                  item_id: station.id,
                  item_type: "station", // Assuming station for now, could be podcast
                  metadata: station,
                })),
              );

            if (uploadError)
              console.error("Error syncing local favorites:", uploadError);

            // Re-fetch to get the complete list
            const { data: updatedRemote } = await supabase
              .from("user_favorites")
              .select("*")
              .order("created_at", { ascending: false });

            if (updatedRemote) {
              const parsedFavorites = updatedRemote
                .map((f) => f.metadata as Station)
                .filter(Boolean);
              setFavorites(parsedFavorites);
              // Update local storage to match remote
              localStorage.setItem(
                "radio_favorites",
                JSON.stringify(parsedFavorites),
              );
            }
          } else {
            // No new local favorites, just use remote
            const parsedFavorites =
              remoteFavorites
                ?.map((f) => f.metadata as Station)
                .filter(Boolean) || [];
            setFavorites(parsedFavorites);
            localStorage.setItem(
              "radio_favorites",
              JSON.stringify(parsedFavorites),
            );
          }
        } else {
          // No local favorites, just use remote
          const parsedFavorites =
            remoteFavorites
              ?.map((f) => f.metadata as Station)
              .filter(Boolean) || [];
          setFavorites(parsedFavorites);
          localStorage.setItem(
            "radio_favorites",
            JSON.stringify(parsedFavorites),
          );
        }
      } catch (err) {
        console.error("Error syncing favorites:", err);
      } finally {
        setLoading(false);
      }
    };

    syncFavorites();
  }, [user]);

  // Handle toggling favorites
  const toggleFavorite = useCallback(
    async (station: Station) => {
      // IMPORTANT: Check if favorite exists BEFORE optimistic update
      const isFavorite = favorites.some((s) => s.id === station.id);

      // Optimistic update
      setFavorites((prev) => {
        const exists = prev.some((s) => s.id === station.id);
        const newFavorites = exists
          ? prev.filter((s) => s.id !== station.id)
          : [station, ...prev];

        // Always update localStorage immediately
        localStorage.setItem("radio_favorites", JSON.stringify(newFavorites));
        return newFavorites;
      });

      // If logged in, update Supabase
      if (user && supabase) {
        if (isFavorite) {
          // Remove from Supabase
          const { error } = await supabase
            .from("user_favorites")
            .delete()
            .match({ user_id: user.id, item_id: station.id });

          if (error) console.error("Error removing favorite:", error);
        } else {
          // Add to Supabase
          const { error } = await supabase.from("user_favorites").insert({
            user_id: user.id,
            item_id: station.id,
            item_type: "station", // Note: Podcasts are handled via usePodcastSubscription
            metadata: station,
          });

          if (error) console.error("Error adding favorite:", error);
        }
      }
    },
    [user, favorites],
  );

  return { favorites, toggleFavorite, loading };
}
